import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import { AlertCircle, LoaderCircle } from 'lucide-react'
import { api } from '../lib/apiClient'
import type { AppRecord, FormField } from '../types'
import { useApi } from '../hooks/useApi'
import { formDefaults, formPayload } from '../lib/formValues'
import { useModalFormState } from './Modal'

function LookupInput({ field, value, onChange, required, filterValue, initialValue }: { field: FormField; value: unknown; onChange: (value: string) => void; required?: boolean; filterValue?: unknown; initialValue?: unknown }) {
  const endpoint = field.lookupEndpoint ?? ''
  const { data, loading, error } = useApi(() => api.list<AppRecord>(endpoint), [endpoint])
  const needsCurrent = Boolean(value && value === initialValue && data && !data.some((record) => record.id === value))
  const current = useApi<AppRecord | null>(() => needsCurrent ? api.get<AppRecord>(endpoint, String(value)) : Promise.resolve(null), [endpoint, value, needsCurrent])
  const available = needsCurrent && current.data ? [...(data ?? []), current.data] : data ?? []
  const pending = loading || (needsCurrent && current.loading)
  const labelKey = field.lookupLabel ?? 'name'
  const selectRef = useRef<HTMLSelectElement>(null)
  const records = available.filter((record) => !field.lookupFilter || Boolean(filterValue && record[field.lookupFilter.recordKey] === filterValue))
  const missingSelection = Boolean(value && !pending && !records.some((record) => record.id === value))
  useEffect(() => {
    selectRef.current?.setCustomValidity(pending ? `Wait for ${field.label.toLowerCase()} options to load.` : error ? `Could not load ${field.label.toLowerCase()} options. Please retry.` : missingSelection ? `Choose an available ${field.label.toLowerCase()}${field.lookupFilter ? ' for the selected customer' : ''}, or clear this field.` : '')
  }, [pending, error, missingSelection, field])
  const recordLabel = (record: AppRecord) => {
    const label = record[labelKey] || record.name || record.title || record.email || record.phone
    return label ? String(label) : `Unnamed ${field.label.toLowerCase()}`
  }
  return (
    <select ref={selectRef} id={`field-${field.key}`} aria-label={field.label} className="field-input" value={String(value ?? '')} onChange={(event) => onChange(event.target.value)} required={required} aria-busy={pending}>
      <option value="">{pending ? 'Loading…' : error ? 'Could not load options' : `Select ${field.label.toLowerCase()}…`}</option>
      {Boolean(value) && !records.some((record) => record.id === value) && <option value={String(value)}>{pending ? 'Loading current selection…' : 'Current selection unavailable — choose again'}</option>}
      {records.map((record) => <option key={record.id} value={record.id}>{recordLabel(record)}</option>)}
    </select>
  )
}

export function RecordForm({ fields, initialValues, submitLabel, onSubmit, onCancel }: {
  fields: FormField[]
  initialValues?: AppRecord | Record<string, unknown> | null
  submitLabel: string
  onSubmit: (values: Record<string, unknown>) => Promise<void>
  onCancel: () => void
}) {
  const defaults = useMemo(() => formDefaults(fields, initialValues), [fields, initialValues])
  const [values, setValues] = useState<Record<string, unknown>>(defaults)
  const [baseline, setBaseline] = useState(defaults)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const dirty = JSON.stringify(values) !== JSON.stringify(baseline)
  const closeModal = useModalFormState(saving, dirty)
  const visibleFields = fields.filter((field) => !field.showWhen || String(values[field.showWhen.key] ?? '') === field.showWhen.value)

  // A background Opportunity refresh must not reset an in-progress form.
  useEffect(() => { if (!dirty) { setValues(defaults); setBaseline(defaults) } }, [defaults, dirty])

  const change = (key: string, value: unknown) => setValues((current) => ({
    ...current, [key]: value,
    ...Object.fromEntries(fields.filter((field) => field.lookupFilter?.valueKey === key && current[key] !== value).map((field) => [field.key, ''])),
  }))
  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (saving) return
    setSaving(true)
    setError(null)
    try {
      await onSubmit(formPayload(fields, values, initialValues))
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not save this record. Please try again.')
      setSaving(false)
    }
  }

  return (
    <form onSubmit={submit}>
      <div className="max-h-[65vh] overflow-y-auto px-5 py-5 sm:px-6">
        {error && <div className="mb-5 flex items-start gap-2.5 rounded-lg bg-rose-50 px-3.5 py-3 text-sm text-rose-700"><AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />{error}</div>}
        <fieldset disabled={saving} className="grid grid-cols-1 gap-x-5 gap-y-4 border-0 p-0 sm:grid-cols-2">
          {visibleFields.map((field) => (
            <div key={field.key} className={field.fullWidth ? 'sm:col-span-2' : ''}>
              {field.type === 'checkbox' ? (
                <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-slate-200 px-3.5 py-3 hover:bg-slate-50">
                  <input type="checkbox" className="h-4 w-4 rounded border-slate-300 text-pine-700 focus:ring-pine-500" checked={Boolean(values[field.key])} onChange={(event) => change(field.key, event.target.checked)} />
                  <span className="text-sm font-semibold text-slate-700">{field.label}</span>
                </label>
              ) : (
                <>
                  <label className="field-label" htmlFor={`field-${field.key}`}>{field.label}{field.required && <span aria-hidden="true" className="ml-0.5 text-ember-600">*</span>}</label>
                  {field.type === 'select' ? (
                    <select id={`field-${field.key}`} aria-label={field.label} className="field-input" value={String(values[field.key] ?? '')} onChange={(event) => change(field.key, event.target.value)} required={field.required}>
                      {(!field.required || !values[field.key]) && <option value="">Select…</option>}
                      {Boolean(values[field.key]) && !field.options?.some((option) => option.value === values[field.key]) && <option value={String(values[field.key])}>{String(values[field.key])} (current)</option>}
                      {field.options?.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                    </select>
                  ) : field.type === 'lookup' ? (
                    <LookupInput field={field} value={values[field.key]} initialValue={initialValues?.[field.key]} filterValue={field.lookupFilter ? values[field.lookupFilter.valueKey] : undefined} onChange={(value) => change(field.key, value)} required={field.required} />
                  ) : field.type === 'textarea' ? (
                    <textarea id={`field-${field.key}`} aria-label={field.label} className="field-input min-h-[96px] resize-y" placeholder={field.placeholder} value={String(values[field.key] ?? '')} onChange={(event) => change(field.key, event.target.value)} required={field.required} />
                  ) : (
                    <input id={`field-${field.key}`} aria-label={field.label} className="field-input" type={field.type} step={field.type === 'number' ? 'any' : undefined} placeholder={field.placeholder} value={String(values[field.key] ?? '')} onChange={(event) => change(field.key, event.target.value)} required={field.required} />
                  )}
                  {field.hint && <p className="mt-1.5 text-xs text-slate-400">{field.hint}</p>}
                </>
              )}
            </div>
          ))}
        </fieldset>
      </div>
      <div className="flex justify-end gap-2 border-t border-slate-100 bg-slate-50/70 px-5 py-4 sm:px-6">
        <button type="button" className="btn-secondary" onClick={closeModal ?? onCancel} disabled={saving}>Cancel</button>
        <button type="submit" className="btn-primary" disabled={saving}>{saving && <LoaderCircle className="h-4 w-4 animate-spin" />}{submitLabel}</button>
      </div>
    </form>
  )
}
